import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import EscalasModal from './EscalasModal';
import { Plus, Edit, Trash2, Calendar, Users, ListFilter, Search, AlertTriangle, Lock, Unlock, EyeOff, Shield, UserCheck, Globe, Loader2, Link2, Repeat, User, X, Info, ClipboardList, MessageSquare, Copy, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { API, fmtDate, norm } from '@/lib/helpers';

const VISIBILIDADE_OPTIONS = [
  { value: 'privado', label: 'Privado', icon: Shield, desc: 'Somente administradores' },
  { value: 'auxiliares', label: 'Auxiliares', icon: UserCheck, desc: 'Visível para auxiliares' },
  { value: 'publico', label: 'Público', icon: Globe, desc: 'Agenda pública (em breve)' },
];

const FILTROS = [
  { key: 'todos', label: 'Todos', color: 'bg-blue-100 text-blue-600' },
  { key: 'abertos', label: 'Abertos', color: 'bg-green-100 text-green-600' },
  { key: 'fechados', label: 'Fechados', color: 'bg-slate-200 text-slate-600' },
];

export default function EventosTab({ events, onRefresh, onDelete, tiposEvento = [], pessoas = [] }) {
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('todos');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', date: '', description: '', status: '', tipo_evento_id: '', quem_presidiu: '', qtd_musicos: '', recitativos: {}, qtd_ministerio: '', qtd_participantes: '', visibilidade: 'auxiliares', pessoa_id: '', pessoa_nome: '', publico: false });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [closeDialog, setCloseDialog] = useState(false);
  const [closeForm, setCloseForm] = useState({ quem_presidiu: '', qtd_musicos: '', recitativos: {}, qtd_ministerio: '', qtd_participantes: '' });
  const [pessoaSearch, setPessoaSearch] = useState('');
  const [publicoConfirm, setPublicoConfirm] = useState(false);
  const [pessoaDropdown, setPessoaDropdown] = useState(false);
  const [escalasModal, setEscalasModal] = useState(false);
  const [gcalLink, setGcalLink] = useState('');
  const [gcalCopied, setGcalCopied] = useState(false);
  const [copyingWhatsappMsg, setCopyingWhatsappMsg] = useState(false);

  useEffect(() => {
    fetch(`${API}/settings`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}).then(d => {
      setGcalLink(d.gcal_public_agenda?.public_url || '');
    }).catch(() => {});
  }, []);

  // Service functions for dynamic recitative fields (recitativo type + "Recitativo Individual" from outros)
  const [recFunctions, setRecFunctions] = useState([]);
  useEffect(() => {
    fetch(`${API}/service-functions`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(funcs => setRecFunctions(funcs.filter(f => f.active !== false && (f.tipo === 'recitativo' || (f.tipo === 'outros' && (f.nome || '').toLowerCase().includes('recitativo individual'))))))
      .catch(() => {});
  }, []);

  const selectedTipo = tiposEvento.find(t => t.tipo_evento_id === form.tipo_evento_id);
  const isClosed = editing?.status === 'fechado';

  useEffect(() => {
    if (selectedTipo?.tem_recitativos) {
      setForm(prev => {
        const existing = prev.recitativos || {};
        return { ...prev, recitativos: { individual_meninos: existing.individual_meninos ?? '', individual_meninas: existing.individual_meninas ?? '' } };
      });
    }
  }, [form.tipo_evento_id]);

  const counts = {
    todos: events.length,
    abertos: events.filter(e => e.status === 'aberto').length,
    fechados: events.filter(e => e.status !== 'aberto').length,
  };

  const filtered = events.filter(e => {
    if (filter === 'abertos' && e.status !== 'aberto') return false;
    if (filter === 'fechados' && e.status === 'aberto') return false;
    if (search && !norm(e.name).includes(norm(search))) return false;
    return true;
  }).sort((a, b) => {
    const aOpen = a.status === 'aberto' ? 0 : 1;
    const bOpen = b.status === 'aberto' ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    if (aOpen === 0) return new Date(a.date) - new Date(b.date);
    return new Date(b.date) - new Date(a.date);
  });

  const validate = () => {
    const e = {};
    if (!form.tipo_evento_id) e.tipo = 'Obrigatório';
    if (!form.name.trim()) e.name = 'Obrigatório';
    if (!form.date) e.date = 'Obrigatório';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openCloseModal = () => {
    const cf = {
      quem_presidiu: form.quem_presidiu || '',
      qtd_musicos: form.qtd_musicos ?? '',
      qtd_ministerio: form.qtd_ministerio ?? '',
      qtd_participantes: form.qtd_participantes ?? '',
      recitativos: editing?.recitativos || {},
    };
    setCloseForm(cf);
    setCloseDialog(true);
  };

  const getCloseValidationErrors = () => {
    if (!selectedTipo) return [];
    const missing = [];
    if (selectedTipo.tem_presidencia && !(closeForm.quem_presidiu || '').trim()) missing.push('Quem Presidiu');
    if (selectedTipo.tem_musicos && (closeForm.qtd_musicos === '' || closeForm.qtd_musicos === null || closeForm.qtd_musicos === undefined)) missing.push('Qtd. de Músicos');
    if (selectedTipo.tem_ministerio && (closeForm.qtd_ministerio === '' || closeForm.qtd_ministerio === null || closeForm.qtd_ministerio === undefined)) missing.push('Qtd. de Ministério');
    if (selectedTipo.tem_participantes && (closeForm.qtd_participantes === '' || closeForm.qtd_participantes === null || closeForm.qtd_participantes === undefined)) missing.push('Qtd. de Participantes');
    if (selectedTipo.tem_recitativos) {
      const rec = closeForm.recitativos || {};
      recFunctions.forEach(f => {
        if (rec[f.function_id] === '' || rec[f.function_id] === undefined || rec[f.function_id] === null) missing.push(f.nome);
      });
    }
    return missing;
  };

  const buildCloseRecitativosPayload = () => {
    const rec = closeForm.recitativos || {};
    const payload = {};
    recFunctions.forEach(f => {
      payload[f.function_id] = rec[f.function_id] !== '' && rec[f.function_id] !== undefined ? parseInt(rec[f.function_id]) : null;
    });
    return payload;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        status: editing?.status || 'aberto',
        qtd_musicos: form.qtd_musicos !== '' ? parseInt(form.qtd_musicos) : null,
        qtd_ministerio: form.qtd_ministerio !== '' ? parseInt(form.qtd_ministerio) : null,
        qtd_participantes: form.qtd_participantes !== '' ? parseInt(form.qtd_participantes) : null,
        recitativos: selectedTipo?.tem_recitativos ? buildRecitativosPayload() : null,
      };
      const url = editing ? `${API}/events/${editing.event_id}` : `${API}/events`;
      const r = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail || 'Erro'); }
      toast.success(editing ? 'Evento atualizado!' : 'Evento criado!');
      closeMainDialog(); onRefresh();
    } catch (err) { toast.error(err.message || 'Erro ao salvar evento'); } finally { setSaving(false); }
  };

  const handleCloseEvent = async () => {
    const missing = getCloseValidationErrors();
    if (missing.length > 0) {
      toast.error(`Preencha todos os campos obrigatórios`);
      return;
    }
    // Check service schedule confirmation if applicable
    if (selectedTipo?.permite_escala_servicos && editing?.event_id) {
      try {
        const schedR = await fetch(`${API}/escalas/service-schedules/${editing.event_id}`, { credentials: 'include' });
        if (schedR.ok) {
          const sched = await schedR.json();
          if (sched.status !== 'confirmada') {
            toast.error('A escala de serviço precisa estar confirmada para fechar este evento.');
            return;
          }
        }
      } catch { /* ignore */ }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        status: 'fechado',
        quem_presidiu: closeForm.quem_presidiu,
        qtd_musicos: closeForm.qtd_musicos !== '' ? parseInt(closeForm.qtd_musicos) : null,
        qtd_ministerio: closeForm.qtd_ministerio !== '' ? parseInt(closeForm.qtd_ministerio) : null,
        qtd_participantes: closeForm.qtd_participantes !== '' ? parseInt(closeForm.qtd_participantes) : null,
        recitativos: selectedTipo?.tem_recitativos ? buildCloseRecitativosPayload() : null,
      };
      const r = await fetch(`${API}/events/${editing.event_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail || 'Erro'); }
      toast.success('Evento fechado!');
      setCloseDialog(false);
      // Refresh and reopen with updated data
      const eventsR = await fetch(`${API}/events/stats`, { credentials: 'include' });
      if (eventsR.ok) {
        const eventsData = await eventsR.json();
        const updated = eventsData.find(e => e.event_id === editing.event_id);
        if (updated) openEdit(updated);
      }
      onRefresh();
    } catch (err) { toast.error(err.message || 'Erro ao fechar evento'); } finally { setSaving(false); }
  };

  const handleReopenEvent = async () => {
    try {
      const { recitativos: _rec, ...formWithoutRec } = form;
      const payload = {
        ...formWithoutRec,
        status: 'aberto',
        qtd_musicos: form.qtd_musicos !== '' ? parseInt(form.qtd_musicos) : null,
        qtd_ministerio: form.qtd_ministerio !== '' ? parseInt(form.qtd_ministerio) : null,
        qtd_participantes: form.qtd_participantes !== '' ? parseInt(form.qtd_participantes) : null,
      };
      const r = await fetch(`${API}/events/${editing.event_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail || 'Erro'); }
      toast.success('Evento reaberto!');
      // Refresh and reopen with updated data
      const eventsR = await fetch(`${API}/events/stats`, { credentials: 'include' });
      if (eventsR.ok) {
        const eventsData = await eventsR.json();
        const updated = eventsData.find(e => e.event_id === editing.event_id);
        if (updated) openEdit(updated);
      }
      onRefresh();
    } catch (err) { toast.error(err.message || 'Erro ao reabrir evento'); }
  };

  const getCloseDeadlineInfo = () => {
    if (!editing || !selectedTipo?.fechamento_valor || !editing.date) return { canClose: true, label: '' };
    try {
      const eventDate = new Date(editing.date);
      if (isNaN(eventDate.getTime())) return { canClose: true, label: '' };
      const delta = selectedTipo.fechamento_unidade === 'dias' ? selectedTipo.fechamento_valor * 24 * 60 * 60 * 1000 : selectedTipo.fechamento_valor * 60 * 60 * 1000;
      const deadline = new Date(eventDate.getTime() + delta);
      const now = new Date();
      if (now >= deadline) return { canClose: true, label: '' };
      const diffMs = deadline - now;
      const diffH = Math.ceil(diffMs / (1000 * 60 * 60));
      const label = diffH >= 24 ? `Podera ser fechado em ${Math.ceil(diffH / 24)} dia(s)` : `Podera ser fechado em ${diffH} hora(s)`;
      return { canClose: false, label };
    } catch { return { canClose: true, label: '' }; }
  };

  const buildRecitativosPayload = () => {
    const rec = form.recitativos || {};
    return { individual_meninos: rec.individual_meninos !== '' ? parseInt(rec.individual_meninos) : null, individual_meninas: rec.individual_meninas !== '' ? parseInt(rec.individual_meninas) : null };
  };

  const openAdd = () => { setEditing(null); setForm({ name: '', date: '', description: '', status: '', tipo_evento_id: '', quem_presidiu: '', qtd_musicos: '', recitativos: {}, qtd_ministerio: '', qtd_participantes: '', visibilidade: 'auxiliares', pessoa_id: '', pessoa_nome: '', publico: false }); setPessoaSearch(''); setErrors({}); setDialog(true); };
  const openEdit = (e) => {
    const rec = e.recitativos || {};
    const formRec = { individual_meninos: rec.individual_meninos ?? '', individual_meninas: rec.individual_meninas ?? '' };
    setEditing(e);
    setForm({ name: e.name, date: e.date || '', description: e.description || '', status: e.status || 'aberto', tipo_evento_id: e.tipo_evento_id || '', quem_presidiu: e.quem_presidiu || '', qtd_musicos: e.qtd_musicos ?? '', recitativos: formRec, qtd_ministerio: e.qtd_ministerio ?? '', qtd_participantes: e.qtd_participantes ?? '', visibilidade: e.visibilidade || (e.privado ? 'privado' : 'auxiliares'), pessoa_id: e.pessoa_id || '', pessoa_nome: e.pessoa_nome || '', publico: e.publico || false });
    setPessoaSearch(e.pessoa_nome || '');
    setErrors({}); setDialog(true);
  };
  const closeMainDialog = () => { setDialog(false); setEditing(null); setErrors({}); setEscalasModal(false); };

  const copyEventWhatsappMessage = async () => {
    if (!editing?.event_id) return;
    setCopyingWhatsappMsg(true);
    try {
      const r = await fetch(`${API}/events/${editing.event_id}/whatsapp-message`, { credentials: 'include' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erro ao gerar mensagem');
      await navigator.clipboard.writeText(data.message || '');
      toast.success('Mensagem WhatsApp copiada!');
    } catch (err) {
      toast.error(err.message || 'Erro ao copiar mensagem');
    } finally {
      setCopyingWhatsappMsg(false);
    }
  };

  const getTipoNome = (tipoId) => tiposEvento.find(t => t.tipo_evento_id === tipoId)?.nome;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Eventos</h2>
                  <Badge className="bg-blue-100 text-blue-700 text-sm font-bold">{counts.todos}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por status</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="eventos-menu">
              {FILTROS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filtro-evento-${f.key}`}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.color.split(' ')[0]}`}>
                      <ListFilter className={`w-4 h-4 ${f.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-blue-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {gcalLink && (
                <Button size="sm" variant="outline" className="w-full text-xs gap-1.5" onClick={() => { navigator.clipboard.writeText(gcalLink); setGcalCopied(true); toast.success('Link copiado!'); setTimeout(() => setGcalCopied(false), 2000); }} data-testid="copy-gcal-link-btn">
                  {gcalCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {gcalCopied ? 'Copiado!' : 'Copiar link Google Agenda'}
                </Button>
              )}
              <Button size="sm" onClick={openAdd} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="add-event-btn">
                <Plus className="w-4 h-4 mr-1" />Novo Evento
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'evento' : 'eventos'}</p>
              </div>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="eventos-search" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0" data-testid="events-list">
              {filtered.map((ev) => {
                const freq = ev.frequencia_pct ?? 0;
                const presentes = ev.presentes ?? 0;
                const totalPessoas = ev.total_pessoas ?? 0;
                const freqColor = freq >= 70 ? '#10B981' : freq >= 40 ? '#F59E0B' : '#EF4444';
                const tipoNome = getTipoNome(ev.tipo_evento_id);
                return (
                  <div key={ev.event_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`event-${ev.event_id}`}>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${ev.status === 'aberto' ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}>
                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="font-semibold text-sm text-slate-900 truncate">{ev.name}</p>
                          <Badge className={`text-xs ${ev.status === 'aberto' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{ev.status === 'aberto' ? 'Aberto' : 'Fechado'}</Badge>
                          {tipoNome && <Badge variant="outline" className="text-xs">{tipoNome}</Badge>}
                          <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-xs px-1.5">{(ev.visibilidade === 'privado' || (!ev.visibilidade && ev.privado)) ? <Shield className="w-3 h-3" /> : ev.visibilidade === 'publico' ? <Globe className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}</Badge></TooltipTrigger><TooltipContent><p className="text-xs">{(ev.visibilidade === 'privado' || (!ev.visibilidade && ev.privado)) ? 'Privado' : ev.visibilidade === 'publico' ? 'Público' : 'Auxiliares'}</p></TooltipContent></Tooltip></TooltipProvider>
                          {ev.auto_recorrencia && <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-xs px-1.5"><Repeat className="w-3 h-3" /></Badge></TooltipTrigger><TooltipContent><p className="text-xs">Evento automático (recorrência)</p></TooltipContent></Tooltip></TooltipProvider>}
                          {(ev.pessoas_nomes?.length > 0 || ev.pessoa_nome) && <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-xs px-1.5"><User className="w-3 h-3" /></Badge></TooltipTrigger><TooltipContent><p className="text-xs">{ev.pessoas_nomes?.length > 0 ? ev.pessoas_nomes.map(n => n.split(' ')[0]).join(', ') : ev.pessoa_nome}</p></TooltipContent></Tooltip></TooltipProvider>}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-600">
                          <span>{fmtDate(ev.date)}</span>
                          <span className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" />
                            <span>{presentes}/{totalPessoas}</span>
                            <div className="w-12 sm:w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(freq, 100)}%`, background: freqColor }}></div></div>
                            <span className="font-semibold" style={{ color: freqColor }}>{freq}%</span>
                          </span>
                        </div>
                        {ev.description && <p className="text-xs text-slate-500 mt-1 hidden sm:block">{ev.description}</p>}
                      </div>
                      <div className="flex gap-0.5 flex-shrink-0 items-center">
                        {ev.reuniao_linked ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="h-8 w-8 flex items-center justify-center"><Link2 className="w-4 h-4 text-slate-400" /></div>
                              </TooltipTrigger>
                              <TooltipContent><p>Vinculado a uma reunião. Administre pela aba Reuniões.</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : ev.visita_id ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="h-8 w-8 flex items-center justify-center"><Link2 className="w-4 h-4 text-slate-400" /></div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs"><p>Vinculado a uma solicitação de visita. Administre pela aba Solic. Visitas.</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (<>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(ev)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => {
                            const tipo = tiposEvento.find(t => t.tipo_evento_id === ev.tipo_evento_id);
                            onDelete('evento', ev.event_id, ev.name, { eventDate: ev.date, tipoEventoId: ev.tipo_evento_id, isRecorrente: ev.auto_recorrencia || tipo?.recorrencia_ativa });
                          }}><Trash2 className="w-4 h-4" /></Button>
                        </>)}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum evento {filter !== 'todos' ? filter.slice(0, -1) : ''}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialog} onOpenChange={(o) => { if (!o) closeMainDialog(); else setDialog(o); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="admin-event-form-dialog">
          <DialogHeader><DialogTitle>{editing ? (editing.status === 'fechado' ? 'Evento Fechado' : 'Editar Evento') : 'Novo Evento'}</DialogTitle><DialogDescription className="sr-only">Formulário de evento</DialogDescription></DialogHeader>
          <div className="space-y-5">
            {/* When closed, show only summary + reopen */}
            {isClosed && (() => {
              const tipoNome = selectedTipo?.nome || 'Evento';
              const dateFmt = fmtDate(form.date);
              const dateParts = dateFmt.split(' ');
              const dateOnly = dateParts[0] || '';
              const timeOnly = dateParts[1] || '';

              const presencaCount = editing?.presentes ?? editing?.total_pessoas_snapshot ?? 0;
              const qtdMinisterio = parseInt(form.qtd_ministerio) || 0;
              const qtdMusicos = parseInt(form.qtd_musicos) || 0;
              const qtdParticipantes = parseInt(form.qtd_participantes) || 0;
              const presidiu = form.quem_presidiu || '';

              let totalRecitativos = 0;
              if (selectedTipo?.tem_recitativos) {
                const rec = form.recitativos || {};
                totalRecitativos += (parseInt(rec.individual_meninos) || 0) + (parseInt(rec.individual_meninas) || 0);
              }

              return (
                <>
                  <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="event-summary">
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Evento de <strong>{tipoNome}</strong> (<strong>{form.name}</strong>) ocorrido em <strong>{dateOnly}{timeOnly ? ` às ${timeOnly}` : ''}</strong>
                      {selectedTipo?.tem_presidencia && presidiu ? <>, presidido pelo irmão <strong>{presidiu}</strong></> : ''}
                      {presencaCount > 0 ? <>, teve a presença registrada de <strong>{presencaCount} jovens e crianças</strong></> : ''}
                      {selectedTipo?.tem_ministerio ? <>, totalizando <strong>{qtdMinisterio} irmão(s) do ministério</strong></> : ''}
                      {selectedTipo?.tem_musicos ? <>, <strong>{qtdMusicos} músico(s)</strong></> : ''}
                      {selectedTipo?.tem_participantes ? <>, <strong>{qtdParticipantes} participante(s)</strong></> : ''}
                      {selectedTipo?.tem_recitativos ? <> e o total de <strong>{totalRecitativos} recitativo(s)</strong></> : ''}.
                    </p>
                  </div>
                  <div className="mt-4">
                    <Button onClick={handleReopenEvent} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="reopen-event-btn"><Unlock className="w-4 h-4 mr-1.5" />Reabrir Evento</Button>
                  </div>
                </>
              );
            })()}

            {/* When open or creating, show two-column layout */}
            {!isClosed && (<>
              <div className="space-y-4">
            {tiposEvento.length > 0 && (
              <div>
                <Label className="text-sm font-semibold text-slate-800 mb-2 block">Tipo de Evento *</Label>
                <div className={`grid gap-2 ${tiposEvento.length <= 3 ? `grid-cols-${tiposEvento.length}` : 'grid-cols-3'}`}>
                  {tiposEvento.map(t => {
                    const active = form.tipo_evento_id === t.tipo_evento_id;
                    return (
                      <button key={t.tipo_evento_id} type="button" onClick={() => setForm({ ...form, tipo_evento_id: t.tipo_evento_id })}
                        className={`py-1.5 px-2 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 border-slate-200 bg-white hover:border-slate-300`}
                        style={active ? { backgroundColor: '#f0f6ff', borderColor: '#2662eb' } : {}}
                        data-testid={`tipo-evento-${t.tipo_evento_id}`}>
                        <Calendar className={`w-3.5 h-3.5 ${active ? 'text-[#2662eb]' : 'text-slate-400'}`} />
                        <span className={`text-[11px] font-semibold leading-tight ${active ? 'text-[#2662eb]' : 'text-slate-700'}`}>{t.nome}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.tipo && <p className="text-xs text-red-500 mt-1">{errors.tipo}</p>}
              </div>
            )}

            {selectedTipo?.permite_pessoa && (
              <div className="relative mt-2" data-testid="pessoa-associada-section">
                <div className="flex items-center gap-1.5 mb-2">
                  <Label className="text-sm font-semibold text-slate-800">Quem irá receber o evento</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger>
                      <TooltipContent className="max-w-xs"><p className="text-xs">Este campo não é obrigatório. Ele aparece porque o Tipo de Evento selecionado foi configurado para permitir a associação de uma pessoa.</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {form.pessoa_id ? (
                  <div className="flex items-center gap-2 p-2.5 border border-blue-200 bg-blue-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {form.pessoa_nome?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-slate-900 flex-1 truncate">{form.pessoa_nome}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => { setForm({ ...form, pessoa_id: '', pessoa_nome: '' }); setPessoaSearch(''); }} data-testid="remove-pessoa-associada"><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      value={pessoaSearch}
                      onChange={(e) => { setPessoaSearch(e.target.value); setPessoaDropdown(true); }}
                      onFocus={() => setPessoaDropdown(true)}
                      data-testid="pessoa-search-input"
                    />
                    {pessoaDropdown && pessoaSearch.length >= 2 && (() => {
                      const results = pessoas.filter(p => norm(p.nome_completo).includes(norm(pessoaSearch))).slice(0, 8);
                      if (results.length === 0) return null;
                      return (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="pessoa-search-results">
                          {results.map(p => (
                            <button key={p.pessoa_id} type="button" className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm border-b border-slate-50 last:border-b-0"
                              onClick={() => { setForm({ ...form, pessoa_id: p.pessoa_id, pessoa_nome: p.nome_completo }); setPessoaSearch(p.nome_completo); setPessoaDropdown(false); }}
                              data-testid={`pessoa-option-${p.pessoa_id}`}>
                              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[10px] flex-shrink-0">
                                {p.nome_completo?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                              </div>
                              <span className="truncate">{p.nome_completo}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <div><Label>Nome do evento *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={50} className={errors.name ? 'border-red-500' : ''} data-testid="event-name-input" />{errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}</div>
            <div><Label>Descrição do evento</Label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="event-description-input" /></div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Data e Hora *</Label>
                {form.date && selectedTipo?.notificar_whatsapp && (() => {
                  const dias = selectedTipo.notificacao_dias || [1, 2];
                  const eventDt = new Date(form.date);
                  const labels = dias.sort((a, b) => b - a).map(d => {
                    const notifDt = new Date(eventDt.getTime() - d * 24 * 60 * 60 * 1000);
                    return `${d === 1 ? '1 dia' : d + ' dias'}: ${notifDt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
                  });
                  const auxs = pessoas.filter(p => p.auxiliar_jovens && p.status === 'aprovado' && !p.deleted && p.whatsapp);
                  // Generate pseudo-random delays (seeded by index for consistency)
                  const delays = auxs.map((_, i) => 30 + ((i * 17 + 7) % 26));
                  let acum = 0;
                  return (
                    <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                      <span className="cursor-default"><MessageSquare className="w-3.5 h-3.5 text-slate-400" /></span>
                    </TooltipTrigger><TooltipContent className="max-w-sm"><div className="text-xs space-y-1">
                      <p className="font-semibold">Notificações WhatsApp:</p>
                      {labels.map((l, i) => <p key={i}>{l}</p>)}
                      <div className="border-t border-slate-200 mt-1.5 pt-1.5">
                        <p className="font-semibold mb-1">Destinatários ({auxs.length}):</p>
                        {auxs.slice(0, 15).map((a, i) => {
                          acum += i === 0 ? 0 : delays[i - 1];
                          const mins = Math.floor(acum / 60);
                          const secs = acum % 60;
                          const timeStr = mins > 0 ? `~${mins}m${secs > 0 ? secs + 's' : ''}` : `~${secs}s`;
                          return <p key={i} className="text-slate-500">{a.apelido || a.nome_completo?.split(' ')[0]} <span className="text-slate-400">{i === 0 ? '(imediato)' : timeStr}</span></p>;
                        })}
                        {auxs.length > 15 && <p className="text-slate-400">+{auxs.length - 15} auxiliares...</p>}
                      </div>
                    </div></TooltipContent></Tooltip></TooltipProvider>
                  );
                })()}
              </div>
              <Input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={errors.date ? 'border-red-500' : ''} data-testid="event-date-input" />{errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
            </div>
            <div className="flex items-center justify-between px-3 py-3 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Evento Público</p>
                  <p className="text-[10px] text-slate-400">Visível na página de Agenda</p>
                </div>
              </div>
              <Switch checked={form.publico} onCheckedChange={(checked) => { if (checked) setPublicoConfirm(true); else setForm({ ...form, publico: false }); }} data-testid="toggle-publico" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800 mb-2 block">Visibilidade do evento</Label>
              <div className="grid grid-cols-3 gap-2">
                {VISIBILIDADE_OPTIONS.map(o => {
                  const Icon = o.icon;
                  const active = form.visibilidade === o.value;
                  const disabled = o.value === 'publico';
                  return (
                    <button key={o.value} type="button" onClick={() => !disabled && setForm({ ...form, visibilidade: o.value })}
                      className={`py-2.5 px-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 border-slate-200 bg-white hover:border-slate-300 ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      style={active && !disabled ? { backgroundColor: '#f0f6ff', borderColor: '#2662eb' } : {}}
                      disabled={disabled}
                      data-testid={`visibilidade-${o.value}`}>
                      <Icon className={`w-4 h-4 ${active && !disabled ? 'text-[#2662eb]' : 'text-slate-400'}`} />
                      <span className={`text-[11px] font-semibold leading-tight text-center ${active && !disabled ? 'text-[#2662eb]' : 'text-slate-700'}`}>{o.label}</span>
                      <span className="text-[9px] text-slate-400 leading-tight text-center">{o.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
              </div>

            <div className={`mt-4 ${editing ? 'flex justify-between items-center' : ''}`}>
              {editing && (
                <div className="flex items-center gap-1.5">
                  {editing.status === 'aberto' && (() => {
                    const { canClose, label } = getCloseDeadlineInfo();
                    return (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button onClick={canClose ? openCloseModal : undefined} variant="outline" size="icon" disabled={!canClose}
                                className={`h-9 w-9 ${canClose ? 'border-slate-300 text-slate-700 hover:bg-slate-100' : 'border-slate-200 text-slate-400 cursor-not-allowed'}`}
                                data-testid="close-event-btn">
                                <Lock className="w-4 h-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">{canClose ? 'Fechar Evento' : label}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })()}
                  {selectedTipo?.permite_escala_servicos && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon" onClick={() => setEscalasModal(true)} className="h-9 w-9 bg-blue-600 hover:bg-blue-700" data-testid="open-escalas-modal-btn">
                            <ClipboardList className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent><p className="text-xs">Gerir Escalas</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {editing && selectedTipo && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon" variant="outline" onClick={copyEventWhatsappMessage} disabled={copyingWhatsappMsg} className="h-9 w-9" data-testid="copy-event-whatsapp-message-btn">
                            {copyingWhatsappMsg ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent><p className="text-xs">Copiar mensagem WhatsApp</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              )}
              <Button onClick={handleSave} disabled={saving} className={`bg-blue-600 hover:bg-blue-700 ${!editing ? 'w-full' : ''}`} data-testid="save-event-btn">{saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde...</> : 'Salvar'}</Button>
            </div>
            </>)}
          </div>
        </DialogContent>
      </Dialog>

      {/* CLOSE EVENT MODAL */}
      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Fechar Evento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selectedTipo && (selectedTipo.tem_presidencia || selectedTipo.tem_participantes || selectedTipo.tem_musicos || selectedTipo.tem_ministerio) && (
              <div className="space-y-4 border border-slate-200 rounded-lg p-4">
                <span className="block text-center text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">Informações Adicionais</span>
                {selectedTipo.tem_presidencia && (
                  <div>
                    <Label className="text-xs text-slate-500">Quem Presidiu? *</Label>
                    <Input value={closeForm.quem_presidiu} onChange={(e) => setCloseForm({ ...closeForm, quem_presidiu: e.target.value })} data-testid="close-quem-presidiu" />
                  </div>
                )}
                {(selectedTipo.tem_musicos || selectedTipo.tem_ministerio || selectedTipo.tem_participantes) && (
                  <div className={`grid gap-3 ${[selectedTipo.tem_musicos, selectedTipo.tem_ministerio, selectedTipo.tem_participantes].filter(Boolean).length >= 3 ? 'grid-cols-3' : [selectedTipo.tem_musicos, selectedTipo.tem_ministerio, selectedTipo.tem_participantes].filter(Boolean).length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {selectedTipo.tem_musicos && (
                      <div>
                        <Label className="text-xs text-slate-500">Qtd. de Músicos *</Label>
                        <Input type="number" min="0" value={closeForm.qtd_musicos} onChange={(e) => setCloseForm({ ...closeForm, qtd_musicos: e.target.value })} data-testid="close-qtd-musicos" />
                      </div>
                    )}
                    {selectedTipo.tem_ministerio && (
                      <div>
                        <Label className="text-xs text-slate-500">Qtd. de Ministério *</Label>
                        <Input type="number" min="0" value={closeForm.qtd_ministerio} onChange={(e) => setCloseForm({ ...closeForm, qtd_ministerio: e.target.value })} data-testid="close-qtd-ministerio" />
                      </div>
                    )}
                    {selectedTipo.tem_participantes && (
                      <div>
                        <Label className="text-xs text-slate-500">Qtd. de Participantes *</Label>
                        <Input type="number" min="0" value={closeForm.qtd_participantes} onChange={(e) => setCloseForm({ ...closeForm, qtd_participantes: e.target.value })} data-testid="close-qtd-participantes" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {selectedTipo?.tem_recitativos && (
              <div className="space-y-3 border border-slate-200 rounded-lg p-4 mt-4">
                <span className="block text-center text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none">Contagem de Recitativos</span>
                <div className="grid grid-cols-2 gap-4">
                  {[{ key: 'feminino', label: 'Meninas', labelColor: 'text-pink-700', bgColor: 'bg-pink-50', borderColor: 'border-pink-200' },
                    { key: 'masculino', label: 'Meninos', labelColor: 'text-blue-700', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' }].map(gender => {
                    const genderFuncs = recFunctions.filter(f => (f.eligibility?.gender || 'todos') === gender.key);
                    return (
                      <div key={gender.key} className="space-y-3">
                        <span className={`block text-center text-xs font-semibold ${gender.labelColor} ${gender.bgColor} border ${gender.borderColor} px-2.5 py-1 rounded-full uppercase tracking-wider pointer-events-none`}>{gender.label}</span>
                        {genderFuncs.length === 0 ? (
                          <p className="text-xs text-slate-400 italic text-center">Nenhuma função</p>
                        ) : genderFuncs.map(func => (
                          <div key={func.function_id}>
                            <Label className="text-xs text-slate-500">{func.nome}</Label>
                            <Input type="number" min="0" value={closeForm.recitativos?.[func.function_id] ?? ''} onChange={(e) => setCloseForm(prev => ({ ...prev, recitativos: { ...prev.recitativos, [func.function_id]: e.target.value } }))} data-testid={`close-rec-${func.function_id}`} />
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Button onClick={handleCloseEvent} disabled={saving} className="w-full bg-slate-700 hover:bg-slate-800" data-testid="confirm-close-event-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde...</> : <><Lock className="w-4 h-4 mr-1.5" />Fechar Evento</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {editing && <EscalasModal open={escalasModal} onClose={() => setEscalasModal(false)} eventId={editing?.event_id} eventStatus={editing?.status} pessoas={pessoas} allowedFunctionIds={selectedTipo?.escalas_servico_ids} />}

      <AlertDialog open={publicoConfirm} onOpenChange={setPublicoConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tornar evento público?</AlertDialogTitle>
            <AlertDialogDescription>Este evento será visível na página pública de Agenda da igreja. Qualquer pessoa com o link poderá ver os detalhes do evento.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setForm(prev => ({ ...prev, publico: true })); setPublicoConfirm(false); }} style={{ backgroundColor: '#2662eb' }}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
